// @djsem/analysis-contract — Event & Release Value-Add Layer
// Prompt 43: Event Brief Builder
// Prompt 45: Technical Rider / Venue Handoff
// Prompt 46: Missing Track Finder / Playlist Gap Analysis
// Prompt 47: EPK / Press Kit Generator
// Prompt 52: Brand Consistency Scoring
// Prompt 53: Release Readiness Coach

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";
const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 47 — EPK / Press Kit Generator
// ============================================================================

/** A single public-facing link (social, streaming, website, etc.) associated with an artist profile. */
export interface DJSEMPublicArtistLink {
  provider: string; label: string; url: string; public: boolean;
}

/** An artist's press-kit profile: bio, links, and selected releases/assets used to build EPK packs. */
export interface DJSEMEpkProfile {
  id: string;
  projectId?: string;
  artistName: string;
  tagline?: string;
  genres: string[];
  shortBio?: string;
  longBio?: string;
  bookingEmail?: string;
  publicLinks: DJSEMPublicArtistLink[];
  selectedReleaseIds: string[];
  selectedAssetIds: string[];
  status: "draft" | "review_requested" | "approved" | "published" | "archived";
  warnings: string[];
  createdAt: string;
  updatedAt: string;
}

/** A single content section (bio, links, contact, etc.) within a generated EPK pack. */
export interface DJSEMEpkSection {
  id: string;
  sectionType: string;
  title: string;
  body?: string;
  assetIds: string[];
  approved: boolean;
  warnings: string[];
}

/** A rights-clearance summary for the assets included in an EPK pack, gating public export. */
export interface DJSEMEpkRightsSummary {
  publicExportAllowed: boolean;
  blockedAssetIds: string[];
  aiGeneratedAssetIds: string[];
  unknownRightsAssetIds: string[];
  warnings: string[];
}

/** A generated press kit pack built from an artist profile, ready for review/export. */
export interface DJSEMEpkPack {
  id: string;
  profileId: string;
  title: string;
  status: "draft" | "ready" | "exported" | "published" | "archived";
  sections: DJSEMEpkSection[];
  rightsSummary: DJSEMEpkRightsSummary;
  warnings: string[];
  createdAt: string;
}

/** Input required to create a new {@link DJSEMEpkProfile}. */
export interface CreateEpkProfileInput extends Omit<DJSEMEpkProfile, "id" | "status" | "createdAt" | "updatedAt" | "warnings"> {}

/** Creates and validates artist EPK profiles. */
export class EpkProfileService {
  /**
   * Creates a new draft EPK profile.
   * @param input - the artist's name, bio, links, and selected releases/assets.
   * @returns the newly created profile in `"draft"` status.
   */
  createProfile(input: CreateEpkProfileInput): DJSEMEpkProfile {
    return { ...input, id: `epk_${_seq.next()}`, status: "draft", warnings: [], createdAt: AT, updatedAt: AT };
  }

  /**
   * Validates that a profile has the minimum required fields (artist name, at least one genre).
   * @param profile - the profile to validate.
   * @returns the validation result.
   */
  validateProfile(profile: DJSEMEpkProfile): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!profile.artistName.trim()) issues.push({ path: "$.artistName", message: "required" });
    if (profile.genres.length === 0) issues.push({ path: "$.genres", message: "at least one genre" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "epk_profile");
  }
}

/** Generates and validates EPK packs from artist profiles for public export. */
export class EpkGeneratorService {
  /**
   * Builds a draft EPK pack's sections (bio, links, contact) from a profile.
   * @param profile - the artist profile to build the pack from.
   * @param rightsSummary - the rights clearance summary to attach to the pack.
   * @returns the newly created draft EPK pack.
   */
  createPack(profile: DJSEMEpkProfile, rightsSummary: DJSEMEpkRightsSummary): DJSEMEpkPack {
    const sections: DJSEMEpkSection[] = [
      { id: `es_${_seq.next()}`, sectionType: "bio_short", title: "Short Bio", body: profile.shortBio, assetIds: [], approved: !!profile.shortBio, warnings: profile.shortBio ? [] : ["Short bio missing"] },
      { id: `es_${_seq.next()}`, sectionType: "bio_long", title: "Long Bio", body: profile.longBio, assetIds: [], approved: !!profile.longBio, warnings: profile.longBio ? [] : ["Long bio missing"] },
      { id: `es_${_seq.next()}`, sectionType: "links", title: "Links", body: undefined, assetIds: [], approved: profile.publicLinks.length > 0, warnings: profile.publicLinks.length === 0 ? ["No public links"] : [] },
    ];
    if (profile.bookingEmail) sections.push({ id: `es_${_seq.next()}`, sectionType: "contact", title: "Booking", body: profile.bookingEmail, assetIds: [], approved: true, warnings: [] });
    return { id: `ep_${_seq.next()}`, profileId: profile.id, title: `${profile.artistName} EPK`, status: "draft", sections, rightsSummary, warnings: [], createdAt: AT };
  }

  /**
   * Validates that a pack's rights summary allows public export (no blocked assets, no rights blocks).
   * @param pack - the EPK pack to validate.
   * @returns the validation result.
   */
  validateForPublicExport(pack: DJSEMEpkPack): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!pack.rightsSummary.publicExportAllowed) issues.push({ path: "$.rightsSummary", message: "public export blocked by rights" });
    if (pack.rightsSummary.blockedAssetIds.length > 0) issues.push({ path: "$.rightsSummary.blockedAssetIds", message: `${pack.rightsSummary.blockedAssetIds.length} blocked asset(s)` });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "epk_export");
  }
}
